import React, { useCallback, useEffect, useState } from 'react';
import { RouteErrorBoundary } from './AllieRoute';

const FORBIDDEN_PATH = '/forbidden';

interface UseViewRouteOptions {
  language?: string;
  onError?: (error: Error, errorInfo: any) => void;
}

/**
 * Hook para manejo de rutas con permisos y error boundaries
 * 
 * Proporciona funcionalidad para:
 * - Verificar permisos de usuario para mostrar vistas
 * - Redirigir a página de prohibido si no tiene permisos
 * - Envolver componentes automáticamente con error boundaries
 * - Detectar cambios de usuario para evitar redirecciones durante el proceso
 * 
 * @param options - Configuración opcional para idioma y manejo de errores
 * @returns Objeto con funciones para crear elementos de ruta con manejo de permisos y errores
 */
export const useViewRoute = (options: UseViewRouteOptions = {}) => {
  const { language = 'ES', onError } = options;
  const [isChangingUser, setIsChangingUser] = useState(false);

  // Listen for changes in localStorage to detect user changes
  useEffect(() => {
    const checkChangingUser = () => {
      try {
        const changingUser = localStorage.getItem('isChangingUser');
        setIsChangingUser(changingUser === 'true');
      } catch (error) {
        setIsChangingUser(false);
      }
    };

    // Check initial state immediately
    checkChangingUser();

    // Listen for storage events (for other tabs)
    window.addEventListener('storage', checkChangingUser);
    
    // Check frequently to catch same-tab changes
    const interval = setInterval(checkChangingUser, 50);

    return () => {
      window.removeEventListener('storage', checkChangingUser);
      clearInterval(interval);
    };
  }, []);

  const ForbiddenComponent = () => {
    useEffect(() => { 
      // Double-check localStorage value immediately before redirecting
      const currentChangingUser = localStorage.getItem('isChangingUser') === 'true';
      
      // Don't redirect if user is being changed (check both state and localStorage)
      if (!isChangingUser && !currentChangingUser) {
        window.location.href = FORBIDDEN_PATH;
      }
    }, [isChangingUser]);

    return <></>
  };

  /**
   * Obtiene un componente verificando permisos y envolviendo con error boundary
   * @param condition - Condición de permiso (true = permitido, false/undefined = prohibido)
   * @param view - Componente a mostrar si tiene permisos
   * @returns Elemento React envuelto con error boundary
   */
  const getComponent = useCallback((condition: boolean | undefined, view: React.ReactElement) => (
    condition ? view : <ForbiddenComponent />
  ), []);

  /**
   * Obtiene un elemento de ruta con error boundary integrado
   * Similar a useAllieRouteElement pero específico para rutas con permisos
   * @param condition - Condición de permiso
   * @param view - Componente a mostrar si tiene permisos
   * @returns Elemento React envuelto con error boundary para usar en Route.element
   */
  const getRouteElement = useCallback((
    condition: boolean | undefined, 
    view: React.ReactElement
  ) => {
    const component = getComponent(condition, view);
    
    return (
      <RouteErrorBoundary onError={onError} language={language}>
        {component}
      </RouteErrorBoundary>
    );
  }, [getComponent, onError, language]);

  return { 
    getComponent, 
    getRouteElement,
    isChangingUser 
  };
};

export default useViewRoute;